import { useCallback, useEffect, useRef, useState } from 'react';

import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';

import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { useSearchAccountsLazy } from '@/services/accounts';
import { AccountAutoCompleteItem } from '@/services/lookups/types';

import './AccountAutoComplete.css';

type AccountAutoCompleteProps = AutoCompleteProps & {
  className?: string;
  label?: string;
  defaultValue?: AccountAutoCompleteItem;
  appendTo?: string | null;
  defaultId?: string;
};

export const AccountAutoComplete = ({
  className,
  label,
  defaultValue,
  defaultId,
  appendTo = 'self',
  onSelect,
  onChange: externalOnChange,
  ...autoCompleteProps
}: AccountAutoCompleteProps) => {
  const [selected, setSelected] = useState<AccountAutoCompleteItem | undefined>(
    defaultValue,
  );
  const [filtered, setFiltered] = useState<AccountAutoCompleteItem[]>([]);
  const [inputValue, setInputValue] = useState(
    defaultValue ? `${defaultValue.name} [${defaultValue.code}]` : '',
  );
  const userChangedRef = useRef(false);

  const [query] = useSearchAccountsLazy();

  useEffect(() => {
    if (userChangedRef.current) {
      userChangedRef.current = false;
      return;
    }
    setSelected(defaultValue);
    setInputValue(defaultValue ? `${defaultValue.name}` : '');
  }, [defaultValue]);

  const search = useCallback(
    async (event: { query: string }) => {
      const { data = [] } = await query({
        search: event.query || '',
        selectionLimit: SELECTION_LIMIT,
      });

      setFiltered(
        data.map((account) => ({
          ...account,
          name: `${account.name} [${account.code}]`,
        })),
      );
    },
    [query],
  );

  const handleSelect = (e: { value: AccountAutoCompleteItem }) => {
    userChangedRef.current = true;
    setSelected(e.value);
    setInputValue(`${e.value?.name}` as string);
    if (onSelect) {
      onSelect(e as any);
    }
  };

  const handleChange = (e: { value: string }) => {
    userChangedRef.current = true;
    setInputValue(e.value as string);
    externalOnChange?.(e as any);
  };

  return (
    <div
      className={`[ account-auto-complete ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      {label && <label>{label}</label>}
      <AutoComplete
        appendTo={appendTo}
        suggestions={filtered}
        field="name"
        minLength={MIN_SEARCH_LENGTH}
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
        className={className}
      />
    </div>
  );
};
